import type { ContentLanguage, TranslatedTitles } from "@plannello/shared";

/* Pure part of the title translation display (no React, no app imports: unit tested in displayTitle.test.ts). */

export type DisplayTitle = {
  /** What to render. */
  text: string;
  /** `text` is a translation. */
  translated: boolean;
  /** The title as written. */
  original: string;
  /** The translation was made from an older version of the item. */
  stale: boolean;
  /** Language the original is written in (when known), for the tooltip and screen readers. */
  sourceLanguage: ContentLanguage | null;
};

export type TitleContext = {
  locale: ContentLanguage;
  titles: TranslatedTitles | undefined;
  /** Language an item is written in (its own, else its product's); undefined while unknown. */
  languageOf: (item: TitleSubject) => ContentLanguage | undefined;
};

export type TitleSubject = {
  id: string;
  title: string;
  productId?: string;
  contentLanguage?: ContentLanguage | null;
};

/** The title to show for `item`: its translation into the reader's locale when there is one, else the original. */
export function displayTitle(item: TitleSubject, ctx: TitleContext): DisplayTitle {
  const entry = ctx.titles?.[item.id];
  const lang = ctx.languageOf(item);
  // An entry is only ever for a foreign-language item; a language change not yet reflected in the cache is caught here.
  if (!entry?.title || lang === ctx.locale) {
    return { text: item.title, translated: false, original: item.title, stale: false, sourceLanguage: lang ?? null };
  }
  return {
    text: entry.title,
    translated: true,
    original: item.title,
    stale: entry.status === "stale",
    sourceLanguage: lang ?? null,
  };
}
